// Media library API — presigned multipart uploads + asset reads.
// Part bodies are raw bytes sent to the object store, so putPart uses fetch
// directly instead of the JSON api client.

import { api } from './client'
import type {
  MediaAsset,
  MediaAssetKind,
  MediaAssetListResponse,
  MediaAssetOrigin,
  MediaAssetStatus,
  MediaUploadInitResponse,
  MediaUploadStatusResponse,
} from '@/types/media'

export async function initUpload(params: {
  filename: string
  content_type: string
  total_size: number
  part_size: number
  content_hash?: string
}): Promise<MediaUploadInitResponse> {
  return api.post<MediaUploadInitResponse>('/media/uploads/init', params)
}

// A presigned part URL stopped being valid (expired or rejected) — the upload
// store fetches fresh URLs from the status endpoint and retries.
export class PartUrlExpiredError extends Error {
  constructor() {
    super('Upload link expired')
    this.name = 'PartUrlExpiredError'
  }
}

// PUT one part's raw bytes straight to the object store. The presigned URL is
// the credential, so no Authorization header is sent (it would also trip the
// bucket's CORS preflight).
export async function putPart(url: string, data: Blob): Promise<void> {
  const response = await fetch(url, { method: 'PUT', body: data })
  if (response.status === 403) throw new PartUrlExpiredError()
  if (!response.ok) {
    throw new Error(`Part upload failed (HTTP ${response.status})`)
  }
}

export async function getUploadStatus(uploadId: string): Promise<MediaUploadStatusResponse> {
  return api.get<MediaUploadStatusResponse>(`/media/uploads/${uploadId}`)
}

export async function completeUpload(uploadId: string): Promise<MediaAsset> {
  return api.post<MediaAsset>(`/media/uploads/${uploadId}/complete`)
}

export async function listAssets(params?: {
  kind?: MediaAssetKind
  status?: MediaAssetStatus
  origin?: MediaAssetOrigin[]
  limit?: number
  offset?: number
}): Promise<MediaAssetListResponse> {
  const query = new URLSearchParams()
  if (params?.kind) query.set('kind', params.kind)
  if (params?.status) query.set('status', params.status)
  for (const origin of params?.origin ?? []) query.append('origin', origin)
  if (params?.limit) query.set('limit', String(params.limit))
  if (params?.offset) query.set('offset', String(params.offset))
  const suffix = query.toString() ? `?${query.toString()}` : ''
  // The api client unwraps bare `{items: [...]}` envelopes to an array —
  // normalize both shapes back to {items, total}.
  const response = await api.get<MediaAssetListResponse | MediaAsset[]>(`/media/assets${suffix}`)
  if (Array.isArray(response)) {
    return { items: response, total: response.length }
  }
  return response
}

export async function getAsset(assetId: string): Promise<MediaAsset> {
  return api.get<MediaAsset>(`/media/assets/${assetId}`)
}

export async function deleteAsset(assetId: string): Promise<void> {
  await api.delete(`/media/assets/${assetId}`)
}
